"use client";

import { FiAlertTriangle } from "react-icons/fi";
import { useT } from "@/hooks/useT";

/**
 * Red safety badge for any Polygon deposit-address surface: only USDC /
 * USDC.e / pUSD on Polygon can be received — other tokens or other networks
 * are lost forever. Single source of truth for the wording (i18n
 * `onboarding.fund.acceptedTokens`); rendered in the onboarding fund step, the
 * global "Add Funds" modal, etc. The leading network clause is bolded.
 *
 * Real incidents: users sending from Ethereum mainnet to their Polygon-only
 * deposit wallet. Audit 2026-08-24 (balanceOf over all 366
 * `deposit_wallet_address` rows on mainnet/Base/Arbitrum/Optimism/BSC) found 3
 * stranded, $88.92 total — jkjjr 20 USDC (2026-06-18), frodriguezbraojos 54.24,
 * jett9869 14.68. Only Ethereum mainnet; the other chains were clean.
 *
 * The funds sit untouched at that address but nothing here can move them: the
 * deposit wallet is a CREATE2 proxy that the Polymarket relayer only deploys on
 * Polygon. NOTE — an earlier version of this comment said the factory itself
 * only exists on Polygon; that is wrong. `DepositWalletFactory`
 * 0x00000000000Fb5C9ADea0298D729A0CB3823Cc07 and the implementation
 * 0x58CA52ebe0DadfdF531Cde7062e76746de4Db1eB DO have code on mainnet, and
 * Polymarket ships https://recovery.polymarket.com/ for exactly this case, so
 * treat stranded funds as "recoverable via PM support", not "lost". Local SDK
 * constants no longer reproduce any user's address (PM bumped the
 * implementation), so we cannot derive/deploy it ourselves today.
 *
 * Prevention beats recovery: keep this badge visible anywhere a Polygon-only
 * address is shown or copied.
 */
export default function NetworkWarningBadge({ className = "" }: { className?: string }) {
  const t = useT();
  const msg = t("onboarding.fund.acceptedTokens");
  // Bold the first sentence (the network clause, up to ". ").
  const idx = msg.indexOf(". ");

  return (
    <div
      className={`flex items-start gap-2 px-2.5 py-2 rounded-[5px] bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30 ${className}`}
    >
      <FiAlertTriangle className="w-3.5 h-3.5 text-red-500 dark:text-red-400 mt-px flex-shrink-0" />
      <p className="text-[10.5px] leading-snug text-red-600 dark:text-red-400 whitespace-pre-line">
        {idx === -1 ? (
          msg
        ) : (
          <>
            <span className="font-semibold">{msg.slice(0, idx + 1)}</span>{" "}
            {msg.slice(idx + 2)}
          </>
        )}
      </p>
    </div>
  );
}
